import type { TextCardElement } from "@/components/TextCard/TextCard";
import { requireDefined } from "@/utils/envs";
import { WPClient } from "@/wp/client/WPClient";
import type {
  CardGridAcf,
  CardGridSettings,
  CardGridSource,
} from "./cardGridSource";
import {
  getCardGridCatData,
  type CardGridCategory,
} from "./getCardGridCatData";

interface FetchCardGridPostsProps {
  source: CardGridSource;
  settings: CardGridSettings;
  page: number;
  postsPerPage: number;
  offset: number;
  locale: string | undefined;
  abortController?: AbortController;
  categoryOverride?: number[];
}

type CardGridPost = {
  id: number;
  link: string;
  acf?: CardGridAcf;
};

const getCategoryIdsWithDescendants = (
  selectedCategoryIds: number[],
  categories: CardGridCategory[],
) => {
  const categoryIds = new Set(selectedCategoryIds);
  const pendingCategoryIds = [...selectedCategoryIds];

  while (pendingCategoryIds.length > 0) {
    const parentCategoryId = pendingCategoryIds.shift();

    categories.forEach((category) => {
      if (
        category.parent === parentCategoryId &&
        !categoryIds.has(category.id)
      ) {
        categoryIds.add(category.id);
        pendingCategoryIds.push(category.id);
      }
    });
  }

  return [...categoryIds];
};

/* jscpd:ignore-start */
export const fetchCardGridPosts = async ({
  source,
  settings,
  page,
  postsPerPage,
  offset,
  locale,
  abortController,
  categoryOverride,
}: FetchCardGridPostsProps): Promise<{
  posts: TextCardElement[];
  totalPosts: number;
}> => {
  const defaultLocale = requireDefined(process.env.NEXT_PUBLIC_DEFAULT_LOCALE);
  const client = new WPClient({ locale, defaultLocale });

  const { categories, posts: selectedPosts, type } = settings;
  const selectedPostIds = Array.isArray(selectedPosts) ? selectedPosts : [];
  const categoryIds =
    categoryOverride ?? (Array.isArray(categories) ? categories : []);
  const shouldFilterByCategory =
    (type === "categories" || categoryOverride) && categoryIds.length > 0;
  const filterCategoryIds = shouldFilterByCategory
    ? getCategoryIdsWithDescendants(
        categoryIds,
        (
          await getCardGridCatData({
            locale,
            taxonomy: source.taxonomy,
          })
        ).categories,
      )
    : categoryIds;

  if (type === "posts" && selectedPostIds.length === 0) {
    return {
      posts: [],
      totalPosts: 0,
    };
  }

  const postsResult = await client.getGenericEndpoint<CardGridPost, []>({
    endpoint: source.postType,
    page,
    perPage: postsPerPage,
    offset,
    fields: [],
    ...(type === "posts" && {
      includeIds: { ids: selectedPostIds, keepOrder: true },
    }),
    ...(shouldFilterByCategory &&
      filterCategoryIds.length > 0 && {
        customParams: {
          [source.taxonomy]: filterCategoryIds.join(","),
        },
      }),
    abortController,
  });

  const posts = postsResult.elements.map((post) =>
    source.mapPostToCard(post.acf),
  );

  return {
    posts,
    totalPosts: postsResult.totalPosts,
  };
};
/* jscpd:ignore-end */
